import type { FoxItField, FoxItPDFForm } from '@/FoxItTypes'
import { FoxItFieldTypes } from '@/FoxItTypes'
import FormFactory from '@/components/documents/Foxit/FormMapping/FormFactory'
import { isFuneralDirectorSignature } from '@/components/documents/Foxit/FoxItHelpers'
import { useFoxitInstance } from '@/components/documents/Foxit/composables/FoxitInstance'
import { useFoxitUI } from '@/components/documents/Foxit/composables/FoxitUI'
import { useRequiredFieldsProvider } from '@/components/documents/Foxit/composables/RequiredFieldsProvider'
import { DocumentType } from '@/enums/DocumentType'
import { PdfCheckboxValue } from '@/enums/PdfCheckboxValue'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import type { CartItemAndLineItem } from '@/mixins/merchandise'
import { usePaymentOptions } from '@/mixins/paymentOptions'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { CustomCartItem } from '@/types'
import type { ScenarioOutput } from '@precoa/calculator-sdk'
import { ref } from 'vue'

const devMode = import.meta.env.VITE_FORM_FILLING_DEV_MODE === 'true'
const validatedSignatures = ref(false)
const validatedFuneralDirectorSignatures = ref(false)
const validatedRequiredFields = ref(false)
const unfilledRequiredFields = ref<string[]>([])
const validateModalOpen = ref(false)

export const useFoxitFormFilling = () => {
	const { t } = usePresentationLocale()
	const sessionStore = useSessionStore()
	const planStore = usePlanStore()
	const sessionDocumentsStore = useSessionDocumentsStore()
	const { getPlanPaymentOptions } = usePaymentOptions()
	const { getPlanCartItems, formatPrice } = useMerchandise()
	const { setRequiredFieldTransparent } = useFoxitUI()
	const { getPdfViewer } = useFoxitInstance()

	// Sets the values of the form fields, happens on first load of a document
	const setSignatureValidationStatus = async () => {
		const pdfViewer = getPdfViewer()
		const doc = pdfViewer.getCurrentPDFDoc()
		const form = (await doc?.loadPDFForm()) as unknown as FoxItPDFForm
		const signatureButtonFields = form.fields.filter(
			(field: FoxItField) => field.info.type === 1
		)

		validatedSignatures.value = !signatureButtonFields.some(
			(field) => !isFuneralDirectorSignature(field.info.name)
		)
		validatedFuneralDirectorSignatures.value = !signatureButtonFields.some(
			(field) => isFuneralDirectorSignature(field.info.name)
		)
	}

	const setRequiredFieldsValidationStatus = async () => {
		unfilledRequiredFields.value = []
		const pdfViewer = getPdfViewer()
		const doc = pdfViewer.getCurrentPDFDoc()
		const pdfForm = (await doc?.loadPDFForm()) as unknown as FoxItPDFForm
		const requiredFields = new Map<string, string>()
		const textFieldLabels = getRequiredTextFieldLabels()

		pdfForm.fields.forEach((field) => {
			if (fieldIsRequired(field.getFlags())) {
				const fieldKey =
					field.info.type === FoxItFieldTypes.text
						? (textFieldLabels.get(field.info.name) ?? field.info.alternateName)
						: field.info.name
				requiredFields.set(fieldKey as string, field.info.value as string)
			}
		})

		tabulateCheckBoxValue(requiredFields, unfilledRequiredFields.value)

		requiredFields.forEach((value, key) => {
			if (!value) {
				unfilledRequiredFields.value.push(key)
			}
		})

		validatedRequiredFields.value = unfilledRequiredFields.value.length === 0
	}

	// Labels for the text fields Sales Studio marks required itself. Those fields do
	// not reliably carry an alternate name, so the modal would list a blank item.
	const getRequiredTextFieldLabels = () => {
		const textFieldsConfig =
			useRequiredFieldsProvider().getRequiredTextFieldConfig(
				sessionDocumentsStore.currentDocument?.meta.type
			)

		return new Map(
			textFieldsConfig.map(({ name, label }) => [name, label] as const)
		)
	}

	const formIsValid = () => {
		return (
			validatedSignatures.value &&
			validatedFuneralDirectorSignatures.value &&
			validatedRequiredFields.value
		)
	}

	const validateForm = async () => {
		if (sessionDocumentsStore.currentStep?.type !== 'document') {
			return finalizeDocument()
		}

		await setSignatureValidationStatus()
		await setRequiredFieldsValidationStatus()

		if (formIsValid()) {
			await finalizeDocument()
		} else {
			validateModalOpen.value = true
		}
	}

	// Groups checkboxes by their name
	const tabulateCheckBoxValue = (
		requiredFields: Map<string, string>,
		unfilledField: string[]
	) => {
		if (!sessionDocumentsStore.currentDocument) {
			return
		}

		// grab the config for the current form
		const checkBoxesConfig =
			useRequiredFieldsProvider().getRequiredCheckBoxConfig(
				sessionDocumentsStore.currentDocument.meta.type
			)

		if (!checkBoxesConfig) {
			return
		}

		checkBoxesConfig.forEach(
			({ regex, name }: { regex: RegExp; name: string }) => {
				// for each checkbox group
				const checkBoxValues: string[] = []
				requiredFields.forEach((value, key) => {
					if (regex.test(key)) {
						checkBoxValues.push(value)
						requiredFields.delete(key)
					}
				})
				const checkBoxFilled = checkBoxValues.some(
					(value) => value === PdfCheckboxValue.Checked
				)

				// if none of the checkBoxes is 'Yes', we'd push a user-friendly name into the unfilled list
				if (checkBoxValues.length > 0 && !checkBoxFilled) {
					unfilledField.push(name)
				}
			}
		)
	}

	// Checks if a field's second last bit is on, which is the Required Flag
	const fieldIsRequired = (flag: number): Boolean => {
		return ((flag >> 1) & 1) === 1
	}

	// Builds the form class that maps the current plan's data onto the current document
	const getCurrentForm = () => {
		const currentPlanHolder = sessionStore.getPlanHolder(
			sessionDocumentsStore.currentPlanHolderID
		)

		const stateCode = sessionStore.activeFh?.billingAddress?.stateCode

		if (
			!currentPlanHolder ||
			!sessionDocumentsStore.currentDocument ||
			!stateCode
		) {
			return null
		}

		// Get cart items for GoodAndServicesForm
		let cartItems: CartItemAndLineItem[] = []
		if (
			sessionDocumentsStore.currentStep!.label ===
			DocumentType.StatementOfGoodsAndServices
		) {
			cartItems =
				getPlanCartItems(sessionDocumentsStore.currentPlanHolderID) ?? []
		}

		let enrollmentPaymentDetails: ScenarioOutput | undefined

		// Get Payment Options for Enrolment Form or Payment Auth Form
		if (
			FormFactory.formsNeedingScenarioOutput.includes(
				sessionDocumentsStore.currentStep!.label as DocumentType
			)
		) {
			const planHolder = sessionStore.getPlanHolder(
				sessionDocumentsStore.currentPlanHolderID
			)
			const scenarioOutput = getPlanPaymentOptions(planHolder!).scenarioOutput
				.value

			if (scenarioOutput && !('errors' in scenarioOutput)) {
				enrollmentPaymentDetails = scenarioOutput
			}
		}

		const currentForm = FormFactory.getFormClass(
			stateCode,
			sessionDocumentsStore.currentDocument!.meta.type,
			sessionDocumentsStore.currentPlanHolderID,
			formatPrice,
			t,
			cartItems,
			sessionStore.activePriceBookVersion,
			enrollmentPaymentDetails
		)

		if (!currentForm) {
			console.warn(
				`Could not find form mappings for ${sessionDocumentsStore.currentDocument!.meta.type}`
			)
		}

		return currentForm
	}

	const fillFields = async (
		formMappings: { name: string; value: unknown }[]
	) => {
		const pdfViewer = getPdfViewer()
		const doc = pdfViewer.getCurrentPDFDoc()
		const form = (await doc?.loadPDFForm()) as unknown as FoxItPDFForm

		if (devMode) {
			console.log(form.fields.map((field) => field.info.name))
		}

		for (const fieldValue of formMappings) {
			let field = form?.getField(fieldValue.name)

			// if lineItem's shortDescription is null, set to empty string
			// else field will be filled with 'null' string
			if (field && fieldValue.value !== undefined) {
				field?.setValue(String(fieldValue.value) ?? '')

				setRequiredFieldTransparent(fieldValue.name)
			}
		}

		return form
	}

	const setFieldValues = async () => {
		const currentForm = getCurrentForm()

		if (!currentForm) {
			return
		}

		const formMappings = currentForm.getFormMappings()

		try {
			await fillFields(formMappings)

			sessionDocumentsStore.setStepAutoFilled(
				sessionDocumentsStore.currentStep!,
				true
			)
		} catch (e) {
			return
		}
	}

	// Fills only the fields a newly added custom item belongs on, keeping what the AFP entered by hand.
	// Forms that can't target a custom item's fields are filled in full.
	const setCustomItemFieldValues = async (
		customItemId: CustomCartItem['id']
	) => {
		const currentForm = getCurrentForm()

		if (!currentForm || !('getCustomItemFormMappings' in currentForm)) {
			return setFieldValues()
		}

		const formMappings: { name: string; value: unknown }[] =
			currentForm.getCustomItemFormMappings(customItemId)

		try {
			const form = await fillFields(formMappings)
			const filledField = formMappings
				.map(({ name }) => form?.getField(name))
				.find(Boolean)

			// setValue doesn't run the PDF's calculations the way typing in a field does,
			// so run them to update the totals the PDF calculates
			if (filledField) {
				form.calculate(filledField)
			}
		} catch (e) {
			return
		}
	}

	const finalizeDocument = async () => {
		if (sessionDocumentsStore.currentStep?.label === 'confirmation') {
			await planStore.copyPolicyEmailToEverplansEmailForPlanHolder(
				sessionDocumentsStore.currentPlan!
			)
		}

		sessionDocumentsStore.setStepValidated(
			sessionDocumentsStore.currentStep!,
			true
		)
		validateModalOpen.value = false
		await sessionDocumentsStore.handleNextStep()

		const pdfViewer = getPdfViewer()
		pdfViewer.setFormHighlightColor(0x00ffffff, 0x00ffffff)
	}

	return {
		setFieldValues,
		setCustomItemFieldValues,
		validatedSignatures,
		validatedFuneralDirectorSignatures,
		validatedRequiredFields,
		unfilledRequiredFields,
		validateForm,
		finalizeDocument,
		validateModalOpen,
	}
}
